'use client';

import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faSliders,
  faCaretDown,
  faClose,
  faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useEditDrawerData } from 'src/hooks/common/useEditDrawerData';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import { ParentFilterGroup } from 'src/context/reducers/filterSetReducer';
import { EmptyState } from 'src/components';
import { useSocOptions } from 'src/components/FilterSets/SiteOfCare';
import { renderFilterDescription, renderFilterElements } from './helper';

interface PresetsDropdownProps {
  type: 'FILTER';
  location?: string;
  readOnly?: boolean;
  initialSelectedFilter?: ParentFilterGroup | null;
  onFilterSelect?: (filter: ParentFilterGroup | null) => void;
}

const PresetsDropdown = ({
  type,
  location = LOCATION.DISCOVERY,
  readOnly = false,
  initialSelectedFilter = null,
  onFilterSelect,
}: PresetsDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setCurrentFilterSetData } = useEditDrawerData();
  const { closeAllButCurrent } = useDrawer();
  const { socOptions } = useSocOptions();
  const {
    convertParentFilterSetToFilterSet,
    clearFiltersForParentType,
    tempSelectedFilterPreset,
    setTempSelectedFilterPreset,
  } = useFilterSets(location);

  // Use the filter preset
  const currentTempPreset = tempSelectedFilterPreset;
  const setCurrentTempPreset = setTempSelectedFilterPreset;

  const { filterSets } = useFilterSetUtils({
    filterType: type,
    searchQuery: '',
  });

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'company' | 'personal'>('company');

  // Initialize the temporary selected preset if provided
  useEffect(() => {
    if (initialSelectedFilter && !currentTempPreset) {
      setCurrentTempPreset(initialSelectedFilter);
    }
  }, [initialSelectedFilter, currentTempPreset, setCurrentTempPreset]);

  const { personalSets, companySets } = useMemo(() => {
    if (!filterSets) return { personalSets: [], companySets: [] };

    return {
      personalSets: filterSets.filter((filter) => filter.scope !== 'COMPANY'),
      companySets: filterSets.filter((filter) => filter.scope === 'COMPANY'),
    };
  }, [filterSets]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleEditItem = useCallback(
    (e: React.MouseEvent, item) => {
      e.stopPropagation();
      setCurrentFilterSetData(item);
      closeAllButCurrent(`entityEditFilterDrawer-discovery`);
    },
    [closeAllButCurrent, setCurrentFilterSetData]
  );

  const handlePresets = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleFilterSetClick = (filterSet: ParentFilterGroup) => {
    // Ensure the filter has the correct type property before converting
    const filterWithCorrectType = {
      ...filterSet,
      type: type, // Use the type prop from the component
    };

    convertParentFilterSetToFilterSet(filterWithCorrectType);
    setIsOpen(false);

    // Notify parent component if callback is provided
    if (onFilterSelect) {
      onFilterSelect(filterWithCorrectType);
    }
  };

  const displayText = useMemo(() => {
    if (!currentTempPreset) return 'Choose a preset';

    // Ensure we always display the label regardless of filter type
    return currentTempPreset?.label || 'Choose a preset';
  }, [currentTempPreset]);

  return (
    <div className="relative self-stretch" ref={dropdownRef}>
      <div
        className="w-full px-4 py-2 bg-white rounded-full outline outline-1
          outline-offset-[-1px] outline-neutral-300 inline-flex justify-between items-center cursor-pointer"
        onClick={handlePresets}
        data-testid="presets-dropdown-trigger"
      >
        <div className="flex-1 flex justify-start items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <Icon icon={faSliders} className="w-4 h-3.5 text-neutral-600" />
          </div>
          <div className="flex-1 justify-start text-tertiary text-base font-normal font-['Inter'] leading-normal">
            {displayText}
          </div>
        </div>
        <div
          className={`w-5 h-5 flex items-center justify-center cursor-pointer
              ${currentTempPreset ? 'opacity-100' : 'opacity-0'}
            `}
          onClick={(e) => {
            e.stopPropagation();

            // Clear filters
            clearFiltersForParentType(type);

            // Notify parent component that preset was cleared
            if (onFilterSelect) {
              onFilterSelect(null);
            }
          }}
        >
          <Icon
            icon={faClose}
            className={`w-3 h-1.5 text-neutral-600 transition-all duration-300`}
          />
        </div>
        <div className="w-5 h-5 flex items-center justify-center">
          <Icon
            icon={faCaretDown}
            className={`w-3 h-1.5 text-neutral-600 transition-all duration-300
                        ${isOpen ? '-rotate-90' : 'rotate-0'}
                    `}
          />
        </div>
      </div>
      {isOpen && (
        <div
          className="w-full max-h-96 inset-x-0 top-full mt-1 absolute bg-white rounded-xl shadow-md outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex flex-col justify-start items-start z-50"
          data-testid="presets-dropdown-menu"
        >
          <div className="self-stretch px-8 pt-1 border-b border-neutral-300 inline-flex justify-start items-end gap-4">
            <div
              className="flex-1 py-1 relative flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
              onClick={() => setActiveTab('company')}
              data-testid="company-tab"
            >
              <div className="p-2 rounded-lg flex justify-start items-center gap-1">
                <div
                  className={`text-center justify-center ${
                    activeTab === 'company'
                      ? 'text-primary text-sm font-bold'
                      : 'text-secondary text-sm font-semibold'
                  } font-['Inter'] leading-tight`}
                >
                  Company
                </div>
              </div>
              {activeTab === 'company' && (
                <div className="w-40 h-1 left-0 top-[40px] absolute bg-sky-600 rounded-tl-lg rounded-tr-lg" />
              )}
            </div>
            <div
              className="flex-1 py-1 relative flex justify-center items-center gap-1 overflow-hidden cursor-pointer"
              onClick={() => setActiveTab('personal')}
              data-testid="personal-tab"
            >
              <div className="p-2 rounded-lg flex justify-start items-center gap-1">
                <div
                  className={`text-center justify-center ${
                    activeTab === 'personal'
                      ? 'text-primary text-sm font-bold'
                      : 'text-secondary text-sm font-semibold'
                  } font-['Inter'] leading-tight`}
                >
                  Personal
                </div>
              </div>
              {activeTab === 'personal' && (
                <div className="w-40 h-1 left-0 top-[40px] absolute bg-sky-600 rounded-tl-lg rounded-tr-lg" />
              )}
            </div>
          </div>
          {activeTab === 'company' && (
            <div
              className={`self-stretch p-2 flex flex-col min-h-48 max-h-72 overflow-y-auto ${
                companySets.length === 0
                  ? 'justify-center items-center'
                  : 'justify-start items-start'
              }`}
              data-testid="company-presets-container"
            >
              {companySets.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No Company Presets"
                  description="There are no company-wide presets available."
                  icon={<Icon icon={faSliders} className="text-neutral-400" />}
                />
              ) : (
                <div className="w-full">
                  {companySets.map((filter, index) => {
                    // Compare primarily by label since the type might not be correctly set on the filter
                    const isSelected =
                      currentTempPreset?.label === filter?.label;
                    return (
                      <div
                        key={`${filter.id}-${index}`}
                        className="group self-stretch p-2 rounded-md inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100"
                        onClick={() => handleFilterSetClick(filter as ParentFilterGroup)}
                      >
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                          <div className="inline-flex justify-start items-center gap-2">
                            <div
                              className={`w-4 h-4 relative ${
                                isSelected
                                  ? 'bg-sky-800'
                                  : 'bg-white border border-neutral-400'
                              } rounded-3xl overflow-hidden`}
                            >
                              {isSelected && (
                                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-white rounded-full" />
                              )}
                            </div>
                            <div className="justify-start text-black text-sm font-semibold font-['Inter'] leading-tight">
                              {filter?.label}
                            </div>
                          </div>
                          <div className="self-stretch pl-6 flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start">
                              {renderFilterDescription(
                                filter as ParentFilterGroup,
                                renderFilterElements,
                                socOptions
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="w-4 h-4 relative opacity-0">
                          <div className="w-4 h-4 left-0 top-0 absolute overflow-hidden">
                            <div className="w-3 h-3 left-[1.99px] top-[1.99px] absolute bg-neutral-600" />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {activeTab === 'personal' && (
            <div
              className={`self-stretch p-2 flex flex-col min-h-48 max-h-72 overflow-y-auto ${
                personalSets.length === 0
                  ? 'justify-center items-center'
                  : 'justify-start items-start'
              }`}
              data-testid="personal-presets-container"
            >
              {personalSets.length === 0 ? (
                <EmptyState
                  size="sm"
                  title="No Personal Presets"
                  description="You haven't created any personal presets yet."
                  icon={<Icon icon={faSliders} className="text-neutral-400" />}
                />
              ) : (
                <div className="w-full">
                  {personalSets.map((filter, index) => {
                    // Compare primarily by label since the type might not be correctly set on the filter
                    const isSelected =
                      currentTempPreset?.label === filter?.label;
                    return (
                      <div
                        key={`${filter.id}-${index}`}
                        className="group self-stretch p-2 rounded-md inline-flex justify-start items-center gap-2 cursor-pointer hover:bg-neutral-100"
                        onClick={() => handleFilterSetClick(filter as ParentFilterGroup)}
                      >
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-0.5">
                          <div className="inline-flex justify-start items-center gap-2">
                            <div
                              className={`w-4 h-4 relative ${
                                isSelected
                                  ? 'bg-sky-800'
                                  : 'bg-white border border-neutral-400'
                              } rounded-3xl overflow-hidden`}
                            >
                              {isSelected && (
                                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-white rounded-full" />
                              )}
                            </div>
                            <div className="justify-start text-black text-sm font-semibold font-['Inter'] leading-tight">
                              {filter?.label}
                            </div>
                          </div>
                          <div className="self-stretch pl-6 flex flex-col justify-start items-start gap-1">
                            <div className="self-stretch justify-start">
                              {renderFilterDescription(
                                filter as ParentFilterGroup,
                                renderFilterElements,
                                socOptions
                              )}
                            </div>
                          </div>
                        </div>
                        {!readOnly && (
                          <div
                            className={`w-0 h-5 opacity-0 flex items-start justify-center group-hover:opacity-100 group-hover:w-5`}
                            onClick={(e) => handleEditItem(e, filter)}
                            data-testid="preset-edit-icon"
                          >
                            <Icon
                              icon={faPencil}
                              className="w-4 h-4 text-neutral-600"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {/* Bottom padding */}
          <div className="h-1"></div>
        </div>
      )}
    </div>
  );
};

export default PresetsDropdown;
